import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Upload, Check } from "lucide-react";
import { useContent } from "@/content";
import { Monogram } from "@/components/Monogram";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function AuthCallback({ onDone }) {
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1] || "";
    fetch(`${API}/auth/google/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(typeof d.detail === "string" ? d.detail : "Accesso Google non riuscito");
        }
        window.location.replace("/admin");
      })
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#0A0A0C] px-6 text-center">
      {error ? (
        <>
          <p className="eyebrow">Accesso Google</p>
          <p className="max-w-sm text-sm text-red-300">{error}</p>
          <a href="/admin" className="btn-outline">Torna al login</a>
        </>
      ) : (
        <>
          <div className="animate-pulse"><Monogram size={48} /></div>
          <p className="eyebrow">Accesso con Google in corso…</p>
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("requests");
  const [content, setContent] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [saveState, setSaveState] = useState("");
  const fileInput = useRef(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [copied, setCopied] = useState("");

  const isGoogleCallback = location.hash?.includes("session_id=");

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isGoogleCallback) return; // AuthCallback establishes the session first
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => setUser(u))
      .catch(() => setUser(null));
  }, [isGoogleCallback]);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/content`).then((r) => r.json()).then(setContent);
    if (tab === "requests") {
      fetch(`${API}/admin/submissions`, { credentials: "include" })
        .then((r) => r.json())
        .then(setSubmissions)
        .catch(() => {});
    }
    if (tab === "media") {
      fetch(`${API}/admin/files`, { credentials: "include" })
        .then((r) => r.json())
        .then(setMediaFiles)
        .catch(() => {});
    }
  }, [user, tab]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setMediaError("");
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`${API}/admin/upload`, { method: "POST", credentials: "include", body: fd });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(typeof d.detail === "string" ? d.detail : "Caricamento non riuscito");
        }
      }
      const list = await fetch(`${API}/admin/files`, { credentials: "include" }).then((r) => r.json());
      setMediaFiles(list);
      setCopied("");
    } catch (err) {
      setMediaError(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyMediaUrl = (f) => {
    const absolute = f.url.startsWith("http") ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`;
    navigator.clipboard.writeText(absolute).then(() => {
      setCopied(f.path);
      setTimeout(() => setCopied(""), 2500);
    });
  };
  const mediaUrlText = (f) => (f.url.startsWith("http") ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`);

  if (isGoogleCallback) return <AuthCallback onDone={setUser} />;

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0C]">
        <div className="animate-pulse"><Monogram size={48} /></div>
      </div>
    );
  }

  if (!user) {
    const doLogin = async (e) => {
      e.preventDefault();
      setLoginError("");
      try {
        const u = await fetch(`${API}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        });
        if (!u.ok) {
          const d = await u.json().catch(() => ({}));
          throw new Error(typeof d.detail === "string" ? d.detail : "Accesso non riuscito");
        }
        setUser(await u.json());
      } catch (err) {
        setLoginError(err.message);
      }
    };
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0C] px-6" data-testid="admin-login">
        <form onSubmit={doLogin} className="w-full max-w-sm border hairline bg-[#101014] p-10">
          <div className="mb-8 flex justify-center"><Monogram size={52} /></div>
          <p className="eyebrow text-center">Area riservata</p>
          <h1 className="font-display mt-3 text-center text-2xl font-light text-[#FAF9F6]">Pannello di controllo</h1>
          <label className="label-lux mt-8" htmlFor="a-email">Email</label>
          <input id="a-email" type="email" className="input-lux" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
          <label className="label-lux mt-4" htmlFor="a-pass">Password</label>
          <input id="a-pass" type="password" className="input-lux" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
          {loginError && <p className="mt-4 border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" data-testid="admin-login-error">{loginError}</p>}
          <button type="submit" className="btn-gold mt-8 w-full justify-center" data-testid="admin-login-submit">Accedi</button>
          <div className="mt-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[9px] uppercase tracking-[0.3em] text-[#FAF9F6]/40">oppure</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <button type="button" onClick={googleLogin} className="btn-outline mt-6 w-full justify-center" data-testid="admin-login-google">
            Accedi con Google
          </button>
        </form>
      </div>
    );
  }

  const upd = (path, value) => {
    setContent((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      let cur = clone;
      for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
      cur[path[path.length - 1]] = value;
      return clone;
    });
  };

  const save = async () => {
    setSaveState("Salvataggio…");
    try {
      const res = await fetch(`${API}/admin/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(content),
      });
      if (!res.ok) throw new Error();
      setSaveState("Salvato — il sito è aggiornato");
      setTimeout(() => setSaveState(""), 4000);
    } catch {
      setSaveState("Errore di salvataggio");
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  // Field helpers rendered via direct function calls (stable DOM identity → no focus loss)
  const getPath = (path) => path.reduce((acc, k) => (acc == null ? acc : acc[k]), content);

  const Txt = ({ label, path }) => {
    const value = getPath(path) ?? "";
    return (
      <div>
        <label className="label-lux">{label}</label>
        <input className="input-lux" value={value} onChange={(e) => upd(path, e.target.value)} data-testid={`admin-input-${path.join("-")}`} />
      </div>
    );
  };

  const Bi = ({ label, path, rows = 0 }) => {
    const val = getPath(path) || { it: "", en: "" };
    return (
      <div>
        <label className="label-lux">{label}</label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rows ? (
            <>
              <textarea className="input-lux resize-y" rows={rows} placeholder="Italiano" value={val.it} onChange={(e) => upd([...path, "it"], e.target.value)} data-testid={`admin-bi-${path.join("-")}-it`} />
              <textarea className="input-lux resize-y" rows={rows} placeholder="English" value={val.en} onChange={(e) => upd([...path, "en"], e.target.value)} data-testid={`admin-bi-${path.join("-")}-en`} />
            </>
          ) : (
            <>
              <input className="input-lux" placeholder="Italiano" value={val.it} onChange={(e) => upd([...path, "it"], e.target.value)} data-testid={`admin-bi-${path.join("-")}-it`} />
              <input className="input-lux" placeholder="English" value={val.en} onChange={(e) => upd([...path, "en"], e.target.value)} data-testid={`admin-bi-${path.join("-")}-en`} />
            </>
          )}
        </div>
      </div>
    );
  };

  const BiList = ({ label, path }) => {
    const val = getPath(path) || { it: [], en: [] };
    return (
      <div>
        <label className="label-lux">{label} (una voce per riga)</label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <textarea className="input-lux resize-y" rows={5} placeholder="Italiano" value={val.it.join("\n")} onChange={(e) => upd([...path, "it"], e.target.value.split("\n"))} data-testid={`admin-bilist-${path.join("-")}-it`} />
          <textarea className="input-lux resize-y" rows={5} placeholder="English" value={val.en.join("\n")} onChange={(e) => upd([...path, "en"], e.target.value.split("\n"))} data-testid={`admin-bilist-${path.join("-")}-en`} />
        </div>
      </div>
    );
  };

  if (!content) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0C]"><div className="animate-pulse"><Monogram size={48} /></div></div>;
  }

  const condField = (i, l, key) => {
    const arr = JSON.parse(JSON.stringify(content.conditions.highlights));
    return {
      value: arr[l][i][key] || "",
      onChange: (e) => {
        const next = JSON.parse(JSON.stringify(content.conditions.highlights));
        next[l][i][key] = e.target.value;
        upd(["conditions", "highlights"], next);
      },
    };
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] pb-24" data-testid="admin-panel">
      <header className="sticky top-0 z-20 border-b hairline bg-[#0A0A0C]/95 px-6 py-4 backdrop-blur-md sm:px-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <Monogram size={26} />
            <span className="text-[11px] tracking-[0.3em] text-[#FAF9F6]" style={{ fontFamily: "var(--font-title-solid)" }}>OBSIDIAN · PANNELLO</span>
          </div>
          <div className="flex items-center gap-4">
            {saveState && <span className="text-xs text-[#C5A059]" data-testid="admin-save-state">{saveState}</span>}
            <span className="hidden text-[11px] text-[#A1A1AA] sm:inline">{user.email}</span>
            <button onClick={logout} className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] transition-colors hover:text-[#FAF9F6]" data-testid="admin-logout">Esci</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 pt-10 sm:px-10">
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {[["requests", "Richieste"], ["content", "Contenuti"], ["media", "Immagini"], ["ga4", "GA4 & Impostazioni"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`border px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors ${tab === id ? "border-[#C5A059] text-[#C5A059]" : "hairline text-[#A1A1AA] hover:text-[#FAF9F6]"}`}
              data-testid={`admin-tab-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "requests" && (
          <div className="mt-10 space-y-4" data-testid="admin-requests">
            {submissions.length === 0 && <p className="text-sm text-[#A1A1AA]">Nessuna richiesta ricevuta finora.</p>}
            {submissions.map((s) => (
              <details key={s.ref} className="border hairline bg-[#101014] p-5" data-testid={`admin-submission-${s.ref}`}>
                <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-3">
                  <span className="text-[13px] text-[#FAF9F6]">
                    <span className="font-display text-[#C5A059]">{s.ref}</span> · {s.name} · {s.service && s.service.toUpperCase()} · {s.date} {s.time}
                  </span>
                  <span className={`text-[9px] uppercase tracking-[0.25em] ${s.status === "new" ? "text-[#C5A059]" : "text-[#A1A1AA]"}`}>{s.status}</span>
                </summary>
                <div className="mt-4 grid grid-cols-1 gap-3 text-[12px] text-[#A1A1AA] sm:grid-cols-2">
                  <p><b className="text-[#FAF9F6]/70">Email:</b> <a href={`mailto:${s.email}`} className="text-[#C5A059]">{s.email}</a></p>
                  <p><b className="text-[#FAF9F6]/70">Telefono:</b> {s.phone}</p>
                  <p><b className="text-[#FAF9F6]/70">Da → A:</b> {s.departure} → {s.destination}</p>
                  <p><b className="text-[#FAF9F6]/70">Viaggio:</b> {s.trip_type} · {s.passengers}</p>
                  <p><b className="text-[#FAF9F6]/70">Bagagli:</b> {s.luggage || "-"}</p>
                  <p><b className="text-[#FAF9F6]/70">Volo/treno:</b> {s.flight_train || "-"}</p>
                  <p><b className="text-[#FAF9F6]/70">Bambini:</b> {s.children || "-"}</p>
                  <p><b className="text-[#FAF9F6]/70">Animali:</b> {s.pets || "-"}</p>
                  <p><b className="text-[#FAF9F6]/70">Attrezzatura:</b> {s.equipment || "-"}</p>
                  <p className="sm:col-span-2"><b className="text-[#FAF9F6]/70">Messaggio:</b> {s.message || "-"}</p>
                </div>
                <button
                  onClick={async () => {
                    const ns = s.status === "new" ? "handled" : "new";
                    await fetch(`${API}/admin/submissions/${s.ref}/status`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ status: ns }),
                    });
                    setSubmissions((prev) => prev.map((x) => (x.ref === s.ref ? { ...x, status: ns } : x)));
                  }}
                  className="mt-4 border hairline px-4 py-2 text-[9px] uppercase tracking-[0.25em] text-[#A1A1AA] transition-colors hover:text-[#C5A059]"
                  data-testid={`admin-submission-toggle-${s.ref}`}
                >
                  Segna come {s.status === "new" ? "gestita" : "nuova"}
                </button>
              </details>
            ))}
          </div>
        )}

        {tab === "content" && (
          <div className="mt-10 space-y-14" data-testid="admin-content-editor">
            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Hero</h2>
              {Txt({ label: "Video (URL o percorso)", path: ["hero", "video"] })}
              {Txt({ label: "Poster (immagine di copertina)", path: ["hero", "poster"] })}
              <p className="text-xs font-light leading-relaxed text-[#A1A1AA]">
                L'apertura mostra: logo, occhiello «Il tuo private driver attorno alle Dolomiti» (fisso, IT/EN), il payoff «Move privately. Move exclusively.», i testi qui sotto e due bottoni: «Richiedi un transfer» (WhatsApp) e il numero di Nicky (chiamata diretta).
              </p>
              {Bi({ label: "Testo introduttivo", path: ["hero", "text"], rows: 4 })}
              {Bi({ label: "Microcopy", path: ["hero", "microcopy"], rows: 2 })}
              {Bi({ label: "CTA principale (WhatsApp)", path: ["hero", "cta_primary"] })}
              {Bi({ label: "Payoff", path: ["hero", "payoff"] })}
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Experience / Manifesto</h2>
              {Bi({ label: "Occhiello", path: ["manifesto", "kicker"] })}
              {Bi({ label: "Titolo", path: ["manifesto", "title"] })}
              {Bi({ label: "Primo paragrafo", path: ["manifesto", "text"], rows: 3 })}
              {Bi({ label: "Secondo paragrafo", path: ["manifesto", "text2"], rows: 3 })}
              {Bi({ label: "Citazione", path: ["manifesto", "quote"] })}
              {Bi({ label: "Titolo vantaggi", path: ["manifesto", "advantages_title"] })}
              {BiList({ label: "Vantaggi", path: ["manifesto", "advantages"] })}
              {Txt({ label: "Immagine principale", path: ["manifesto", "image"] })}
              {Txt({ label: "Immagine secondaria", path: ["manifesto", "image_secondary"] })}
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Servizi</h2>
              {Bi({ label: "Occhiello", path: ["services", "kicker"] })}
              {Bi({ label: "Titolo", path: ["services", "title"], rows: 2 })}
              {Bi({ label: "Introduzione", path: ["services", "intro"], rows: 3 })}
              {Bi({ label: "Microcopy", path: ["services", "microcopy"], rows: 2 })}
              {content.services.items.map((item, i) => (
                <div key={item.id} className="space-y-4 border hairline p-5">
                  <p className="eyebrow !text-[9px]">{item.num} · {item.title.it}</p>
                  {Bi({ label: "Titolo", path: ["services", "items", i, "title"] })}
                  {Bi({ label: "Sottotitolo", path: ["services", "items", i, "tagline"], rows: 2 })}
                  {Bi({ label: "Descrizione", path: ["services", "items", i, "desc"], rows: 4 })}
                  {Bi({ label: "Dettaglio (es. aeroporti, itinerari)", path: ["services", "items", i, "detail"], rows: 2 })}
                  {Bi({ label: "CTA", path: ["services", "items", i, "cta"] })}
                  {Txt({ label: "Immagine (es. /media/41.jpg)", path: ["services", "items", i, "image"] })}
                </div>
              ))}
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Night & Dinner (blocco dedicato)</h2>
              {Bi({ label: "Titolo", path: ["night_focus", "headline"], rows: 2 })}
              {Bi({ label: "Testo", path: ["night_focus", "text"], rows: 5 })}
              {Bi({ label: "Firma", path: ["night_focus", "signature"] })}
              {BiList({ label: "Punti di forza", path: ["night_focus", "points"] })}
              {Txt({ label: "Immagine", path: ["night_focus", "image"] })}
              {Bi({ label: "CTA", path: ["night_focus", "cta"] })}
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Viaggiare / Mercedes</h2>
              {Bi({ label: "Occhiello", path: ["vehicle", "kicker"] })}
              {Bi({ label: "Titolo", path: ["vehicle", "title"], rows: 2 })}
              {Bi({ label: "Testo", path: ["vehicle", "text"], rows: 4 })}
              {Bi({ label: "Nota di attenzione", path: ["vehicle", "note"], rows: 3 })}
              {Bi({ label: "Titolo dotazioni", path: ["vehicle", "features_title"] })}
              {BiList({ label: "Dotazioni", path: ["vehicle", "features"] })}
              {Bi({ label: "Titolo pagamenti", path: ["vehicle", "payments_title"] })}
              <div>
                <label className="label-lux">Metodi di pagamento IT (uno per riga)</label>
                <textarea className="input-lux resize-y" rows={6} value={content.vehicle.payments.join("\n")} onChange={(e) => upd(["vehicle", "payments"], e.target.value.split("\n"))} data-testid="admin-payments-it" />
                <label className="label-lux mt-3">Payment methods EN (one per line)</label>
                <textarea className="input-lux resize-y" rows={6} value={content.vehicle.payments_en.join("\n")} onChange={(e) => upd(["vehicle", "payments_en"], e.target.value.split("\n"))} data-testid="admin-payments-en" />
              </div>
              <div>
                <label className="label-lux">Immagini sezione (una per riga)</label>
                <textarea className="input-lux resize-y" rows={3} value={content.vehicle.images.join("\n")} onChange={(e) => upd(["vehicle", "images"], e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} data-testid="admin-vehicle-images" />
              </div>
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Condizioni</h2>
              {Bi({ label: "Occhiello", path: ["conditions", "kicker"] })}
              {Bi({ label: "Titolo", path: ["conditions", "title"] })}
              {Bi({ label: "Testo", path: ["conditions", "text"], rows: 3 })}
              {Bi({ label: "CTA (link ai termini)", path: ["conditions", "cta"] })}
              {content.conditions.highlights.it.map((h, i) => (
                <div key={i} className="space-y-4 border hairline p-5">
                  <p className="eyebrow !text-[9px]">In evidenza {i + 1}</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <input className="input-lux" placeholder="IT titolo" {...condField(i, "it", "title")} data-testid={`admin-cond-title-it-${i}`} />
                    <input className="input-lux" placeholder="EN title" {...condField(i, "en", "title")} data-testid={`admin-cond-title-en-${i}`} />
                    <textarea className="input-lux resize-y" rows={2} placeholder="IT testo" {...condField(i, "it", "body")} data-testid={`admin-cond-body-it-${i}`} />
                    <textarea className="input-lux resize-y" rows={2} placeholder="EN body" {...condField(i, "en", "body")} data-testid={`admin-cond-body-en-${i}`} />
                  </div>
                </div>
              ))}
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Galleria</h2>
              {Bi({ label: "Occhiello", path: ["gallery", "kicker"] })}
              {Bi({ label: "Titolo", path: ["gallery", "title"] })}
              <div>
                <label className="label-lux">Immagini (una per riga)</label>
                <textarea className="input-lux resize-y" rows={6} value={content.gallery.images.join("\n")} onChange={(e) => upd(["gallery", "images"], e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} data-testid="admin-gallery-images" />
              </div>
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Wedding Day / Wedding Transfer</h2>
              {Bi({ label: "Titolo", path: ["wedding", "title"] })}
              {Bi({ label: "Frase di invito", path: ["wedding", "text"], rows: 3 })}
              {Bi({ label: "CTA (bottone WhatsApp)", path: ["wedding", "cta"] })}
            </section>

            <section className="space-y-5">
              <h2 className="font-display text-2xl font-light text-[#C5A059]">Contatti & Form</h2>
              {Bi({ label: "Occhiello", path: ["contact", "kicker"] })}
              {Bi({ label: "Titolo", path: ["contact", "title"] })}
              {Bi({ label: "Testo", path: ["contact", "text"], rows: 3 })}
              {Bi({ label: "Titolo contatti diretti", path: ["contact", "direct_title"] })}
              {Bi({ label: "Testo contatti diretti", path: ["contact", "direct_text"], rows: 2 })}
              {Bi({ label: "Messaggio di ringraziamento post-invio", path: ["contact", "success_msg"], rows: 3 })}
              {Bi({ label: "Etichetta consenso privacy", path: ["contact", "privacy_label"], rows: 2 })}
              {Bi({ label: "Testo bottone invio", path: ["contact", "submit"] })}
              {Txt({ label: "Email contatti", path: ["contacts", "email"] })}
              {Txt({ label: "PEC", path: ["contacts", "pec"] })}
              {Txt({ label: "Numero WhatsApp (formato internazionale senza +)", path: ["contacts", "whatsapp"] })}
              {Bi({ label: "Messaggio precompilato WhatsApp", path: ["contacts", "whatsapp_msg"], rows: 2 })}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="label-lux">Azienda (footer)</label>
                  <input className="input-lux" value={content.footer.company} onChange={(e) => upd(["footer", "company"], e.target.value)} data-testid="admin-footer-company" />
                </div>
                <div>
                  <label className="label-lux">Indirizzo (footer)</label>
                  <input className="input-lux" value={content.footer.address} onChange={(e) => upd(["footer", "address"], e.target.value)} data-testid="admin-footer-address" />
                </div>
                <div>
                  <label className="label-lux">P.IVA</label>
                  <input className="input-lux" value={content.footer.piva} onChange={(e) => upd(["footer", "piva"], e.target.value)} data-testid="admin-footer-piva" />
                </div>
              </div>
            </section>

            <button onClick={save} className="btn-gold w-full justify-center" data-testid="admin-save-content">Salva tutti i contenuti</button>
          </div>
        )}

        {tab === "media" && (
          <div className="mt-10 space-y-8" data-testid="admin-media">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="font-display text-2xl font-light text-[#C5A059]">Immagini del sito</h2>
                <p className="mt-3 max-w-2xl text-[13px] font-light leading-relaxed text-[#A1A1AA]">
                  Carica foto (JPG, PNG, WEBP) o video (MP4, max 25MB): vengono ottimizzati o pubblicati automaticamente.
                  Poi premi "Copia URL" su un file e incollane il collegamento in un campo "Immagine" o "Video" della tab
                  Contenuti, oppure sostituisci le foto della galleria (puoi usare anche video al posto delle foto).
                </p>
              </div>
              <div className="flex flex-col items-start gap-3">
                <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,video/mp4" multiple className="hidden" onChange={onUpload} data-testid="admin-media-input" />
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="btn-gold !px-6 disabled:opacity-50"
                  data-testid="admin-media-upload-btn"
                >
                  <Upload size={14} strokeWidth={1.5} /> {uploading ? "Caricamento…" : "Carica foto"}
                </button>
                {mediaError && <p className="border border-red-400/30 bg-red-400/10 px-4 py-2 text-xs text-red-300" data-testid="admin-media-error">{mediaError}</p>}
              </div>
            </div>

            {mediaFiles.length === 0 ? (
              <p className="text-sm text-[#A1A1AA]">Nessuna foto caricata finora.</p>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {mediaFiles.map((f) => (
                  <div key={f.path} className="border hairline bg-[#101014]" data-testid={`admin-media-item-${f.path.split("/").pop()}`}>
                    <img src={f.url} alt={f.original_filename} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                    <div className="p-3">
                      <p className="truncate text-[10px] text-[#A1A1AA]">{f.original_filename}</p>
                      <button
                        type="button"
                        onClick={() => copyMediaUrl(f)}
                        className={`mt-2 w-full border px-3 py-2 text-[9px] uppercase tracking-[0.25em] transition-colors ${copied === f.path ? "border-[#C5A059] text-[#C5A059]" : "hairline text-[#A1A1AA] hover:text-[#FAF9F6]"}`}
                        data-testid={`admin-media-copy-${f.path.split("/").pop()}`}
                      >
                        {copied === f.path ? <Check size={11} className="mr-1 inline" /> : null} {copied === f.path ? "URL copiata" : "Copia URL"}
                      </button>
                      <input
                        readOnly
                        value={mediaUrlText(f)}
                        onFocus={(e) => e.target.select()}
                        className="mt-2 w-full border hairline bg-transparent px-2 py-1.5 text-[9px] text-[#A1A1AA]"
                        data-testid={`admin-media-url-${f.path.split("/").pop()}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "ga4" && (
          <div className="mt-10 max-w-2xl space-y-6" data-testid="admin-ga4">
            <h2 className="font-display text-2xl font-light text-[#C5A059]">Google Analytics 4</h2>
            <div>
              <label className="label-lux">ID Misurazione GA4 (formato G-XXXXXXXXXX)</label>
              <input
                className="input-lux"
                placeholder="G-XXXXXXXXXX"
                value={content.ga_measurement_id}
                onChange={(e) => upd(["ga_measurement_id"], e.target.value.trim())}
                data-testid="admin-ga4-id"
              />
            </div>
            <button onClick={save} className="btn-gold" data-testid="admin-ga4-save">Salva</button>
            <div className="space-y-3 border hairline p-6 text-[13px] font-light leading-relaxed text-[#A1A1AA]">
              <p className="text-[#FAF9F6]">Come attivare Google Analytics 4 — 4 passaggi:</p>
              <p>1. Crea la casella Gmail dedicata (ad es. analytics.obsidian@gmail.com) da gmail.com → "Crea un account".</p>
              <p>2. Con quella casella, accedi a analytics.google.com → Amministrazione → Crea proprietà → inserisci il sito obsidianprivatedriver.it → crea un "Flusso di dati" di tipo Web.</p>
              <p>3. Copia l'ID Misurazione (formato G-XXXXXXXXXX) e incollalo nel campo qui sopra, poi salva.</p>
              <p>4. Il sito caricherà Google Analytics 4 solo dopo il consenso ai cookie analitici del visitatore (banner GDPR), in forma anonima: nessun intervento tecnico ulteriormente necessario.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
